import { Centrifuge, Subscription } from 'centrifuge';
import type { SSECallbacks } from './sse';
import { fetchCentrifugoToken } from './client';
import { useApprovalStore } from '../stores/useApprovalStore';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

export interface AnalysisConnection {
  disconnect: () => void;
  getStatus: () => ConnectionStatus;
}

// ─── Per-channel singleton registry ─────────────────────────────────────────
// React StrictMode, route transitions (/processing → /generate-report) and
// Vite HMR can all leave a previous subscription alive while a new view tries
// to open another one to the same channel. Both subscriptions then dispatch
// every publication, and because each view's module has its own `_startTime`
// the same event renders twice in the feed with two different elapsed
// timestamps (e.g. `[9.1s]` AND `[37m 13s]`).
//
// Holding at most ONE active connection per channel guarantees a single
// publication path per chat — independent of how many effects/mounts/HMR
// reloads attempted to subscribe.
const _activeByChannel: Map<string, AnalysisConnection> = new Map();

if (typeof import.meta.hot !== 'undefined' && import.meta.hot) {
  // Vite HMR: when this module is replaced, the old `_activeByChannel` Map
  // becomes inaccessible to the new module. Tear connections down explicitly
  // so they don't outlive the module that owns their handlers.
  import.meta.hot.dispose(() => {
    for (const conn of _activeByChannel.values()) {
      try { conn.disconnect(); } catch { /* ignore */ }
    }
    _activeByChannel.clear();
  });
}

/**
 * Dispatch a Centrifugo publication into the same SSECallbacks the SSE
 * transport uses.  The publication `data` has shape:
 *   { event: "stage_started", stage_number: 2, stage_name: "...", ... }
 */
function dispatchPublication(data: Record<string, unknown>, callbacks: SSECallbacks, chatId: string) {
  const event = data.event as string | undefined;
  if (!event) {
    console.warn('[Centrifugo] Publication missing event field:', data);
    return;
  }
  console.debug(`[Centrifugo] Event: ${event}`, data);

  switch (event) {
    case 'approval_required':
      // Cross-cutting: route to the global approval store (see sse.ts).
      useApprovalStore.getState().request(chatId, {
        callId: data.call_id as string,
        name: data.name as string,
        input: data.input as Record<string, unknown>,
      });
      callbacks.onApprovalRequired?.({
        call_id: data.call_id as string,
        name: data.name as string,
        input: data.input as Record<string, unknown>,
      });
      break;
    case 'stage_started':
      callbacks.onStageStarted?.({
        stage_number: data.stage_number as number,
        stage_name: data.stage_name as string,
        total_stages: data.total_stages as number,
      });
      break;
    case 'stage_completed':
      callbacks.onStageCompleted?.({
        stage_number: data.stage_number as number,
        stage_name: data.stage_name as string,
      });
      break;
    case 'text':
      callbacks.onText?.({ content: data.content as string });
      break;
    case 'thinking':
      callbacks.onThinking?.({ content: data.content as string });
      break;
    case 'tool_start':
      callbacks.onToolStart?.({
        name: data.name as string,
        input: data.input as Record<string, unknown> | undefined,
      });
      break;
    case 'tool_result':
      callbacks.onToolResult?.({
        name: data.name as string,
        result: data.result as string,
        is_error: data.is_error as boolean,
      });
      break;
    case 'tool_progress':
      callbacks.onToolProgress?.({
        name: data.name as string,
        message: data.message as string,
      });
      break;
    case 'analysis_recovered':
      callbacks.onAnalysisRecovered?.({
        iteration: data.iteration as number,
        stage_number: data.stage_number as number,
      });
      break;
    case 'done':
      callbacks.onDone?.();
      break;
    case 'error':
      callbacks.onError?.({ message: data.message as string });
      break;
  }
}

/**
 * Create a Centrifugo WebSocket connection to an analysis channel.
 *
 * Centrifugo handles reconnection + message recovery automatically via
 * its history/recovery protocol.
 */
export async function createAnalysisConnection(
  chatId: string,
  callbacks: SSECallbacks,
  onStatusChange?: (status: ConnectionStatus) => void,
  onRecovery?: (recovered: boolean) => void,
  minOffset?: number,
  noRecovery?: boolean,
): Promise<AnalysisConnection> {
  const channel = `analysis:${chatId}`;
  // Hard guarantee: if a prior connection exists for this exact channel,
  // tear it down before opening a new one. This catches view-to-view
  // navigation leaks and HMR resurrection (see _activeByChannel comment).
  const prior = _activeByChannel.get(channel);
  if (prior) {
    console.warn(`[Centrifugo] Disposing prior connection on ${channel} before re-subscribing`);
    try { prior.disconnect(); } catch { /* ignore */ }
    _activeByChannel.delete(channel);
  }

  console.debug('[Centrifugo] Fetching connection token...');
  const { token, ws_url } = await fetchCentrifugoToken();
  console.debug(`[Centrifugo] Token received. WS URL: ${ws_url}`);

  let status: ConnectionStatus = 'connecting';
  const setStatus = (s: ConnectionStatus) => {
    status = s;
    console.debug(`[Centrifugo] Status → ${s}`);
    onStatusChange?.(s);
  };

  const centrifuge = new Centrifuge(ws_url, { token });
  console.debug(`[Centrifugo] Subscribing to channel: ${channel}`);

  const sub: Subscription = centrifuge.newSubscription(channel, noRecovery ? {
    recoverable: false,
  } : undefined);

  // Per-subscription dedup tracker. Lives in the closure of this single
  // factory call, so two parallel `createAnalysisConnection` invocations
  // each have their own counter (this layer is a backstop, not a substitute
  // for the StrictMode guard in the views). Catches the case where Centrifugo
  // recovery flapping or a reconnect re-delivers an offset we've already
  // dispatched on THIS socket.
  let lastSeenOffset = 0;
  sub.on('publication', (ctx) => {
    const data = ctx.data as Record<string, unknown>;
    // Skip recovered events from before minOffset (e.g. analysis events during refine)
    if (minOffset && ctx.offset && ctx.offset <= minOffset) {
      console.debug(`[Centrifugo] Skipping old event offset=${ctx.offset} (min=${minOffset})`);
      return;
    }
    if (ctx.offset && ctx.offset <= lastSeenOffset) {
      console.debug(`[Centrifugo] Dedup: repeated offset=${ctx.offset} (lastSeen=${lastSeenOffset})`);
      return;
    }
    if (ctx.offset) lastSeenOffset = ctx.offset;
    console.debug(`[Centrifugo] Publication on ${channel}: event=${data.event}, offset=${ctx.offset}`);
    dispatchPublication(data, callbacks, chatId);
  });

  sub.on('subscribed', (ctx) => {
    console.debug(`[Centrifugo] Subscribed to ${channel} | recovered: ${ctx.recovered}`);
    onRecovery?.(ctx.recovered);
  });

  sub.on('error', (ctx) => {
    console.error(`[Centrifugo] Subscription error on ${channel}:`, ctx);
  });

  centrifuge.on('connected', (ctx) => {
    console.debug(`[Centrifugo] Connected | client: ${ctx.client}`);
    setStatus('connected');
  });
  centrifuge.on('disconnected', (ctx) => {
    console.warn(`[Centrifugo] Disconnected | code: ${ctx.code} reason: ${ctx.reason}`);
    setStatus('disconnected');
  });
  centrifuge.on('connecting', (ctx) => {
    console.debug(`[Centrifugo] Connecting... | code: ${ctx.code} reason: ${ctx.reason}`);
    setStatus('connecting');
  });
  centrifuge.on('error', (ctx) => {
    console.error('[Centrifugo] Transport error:', ctx);
  });

  sub.subscribe();
  centrifuge.connect();

  let disposed = false;
  const conn: AnalysisConnection = {
    disconnect: () => {
      if (disposed) return;
      disposed = true;
      console.debug(`[Centrifugo] Disconnecting from ${channel}`);
      try { sub.unsubscribe(); } catch { /* ignore */ }
      try { centrifuge.disconnect(); } catch { /* ignore */ }
      setStatus('disconnected');
      // Only remove ourselves from the registry if we're still the
      // active entry — a later createAnalysisConnection() may have
      // replaced us already.
      if (_activeByChannel.get(channel) === conn) {
        _activeByChannel.delete(channel);
      }
    },
    getStatus: () => status,
  };
  _activeByChannel.set(channel, conn);
  return conn;
}
